import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isRevenueDetailsWeekStartAllowed } from '@uo/shared';
import { usersAPI } from '../api/users';
import { useViewAsAccountParams } from '../store/activeAccount';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C } from '../components/ui/ThemedSurface';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { QueryErrorNote } from '../components/ui/QueryErrorNote';

const PAGE_SIZE = 15;

export function IncomeWeekPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const weekType = params.get('type') ?? t('incomeDetails.weekFallback');
  const start = params.get('start') ?? '';
  const end = params.get('end') ?? '';
  const [page, setPage] = useState(1);
  const viewAsParams = useViewAsAccountParams();
  const viewAccountKey = viewAsParams?.account_id ?? 'main';
  const rangeAllowed = !!start && isRevenueDetailsWeekStartAllowed(start);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['users', 'revenue-details', 'week', start, end, page, viewAccountKey],
    queryFn: () =>
      usersAPI.getRevenueDetailsWeek({
        start,
        end,
        page,
        limit: PAGE_SIZE,
        ...viewAsParams,
      }),
    enabled: !!start && !!end && rangeAllowed,
    staleTime: 30_000,
  });

  const rows = data?.rows ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));
  const title = t('incomeDetails.weekTitle', { week: weekType });

  return (
    <div className="min-h-full" style={{ background: '#131f2f' }}>
      <ThemedPageHeader title={title} onBack={() => navigate(-1)} theme={C} />

      <div className="px-5 pb-8 pt-[72px]">
        {!start || !end ? (
          <div className="py-10 text-center text-xs text-white/50">{t('incomeDetails.missingRange')}</div>
        ) : !rangeAllowed ? (
          <div className="py-10 text-center text-xs text-white/50">
            {t('incomeDetails.outOfRange', '仅可查询最近三个月的收益明细')}
          </div>
        ) : (
          <>
            {isError && (
              <QueryErrorNote message={t('incomeDetails.loadFailed')} onRetry={() => void refetch()} />
            )}

            {isLoading ? (
              <div className="space-y-2">
                <LoadingSkeleton className="h-14" />
                <LoadingSkeleton className="h-14" />
                <LoadingSkeleton className="h-14" />
              </div>
            ) : rows.length === 0 ? (
              <div className="py-10 text-center text-xs text-white/50">{t('common.noData')}</div>
            ) : (
              <div className="flex flex-col gap-2">
                {rows.map((row, idx) => (
                  <div
                    key={`${row.remark}-${row.create_time}-${idx}`}
                    className="flex items-start justify-between rounded-2xl px-4 py-3"
                    style={{ background: '#222d3f' }}
                  >
                    <div className="min-w-0 flex-1 pr-3 text-sm text-white">{row.remark}</div>
                    <div className="shrink-0 text-right text-sm text-white">
                      <div>+{row.ep}</div>
                      <div className="mt-1 text-[10px] text-white/55">{row.create_time}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-center gap-3 text-xs text-white/70">
                <button
                  type="button"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded px-2 py-1 disabled:opacity-40"
                  style={{ background: '#222d3f' }}
                >
                  ‹
                </button>
                <span>
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages || isFetching}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded px-2 py-1 disabled:opacity-40"
                  style={{ background: '#222d3f' }}
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default IncomeWeekPage;
